import { useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CreditValue,
  PanelChoiceRow,
  PanelHeader,
  PanelLinkRow,
  PanelRow,
  PanelValueRow,
} from '@/components/settings/panel-rows'

interface Subject {
  id: string
  name: string
  credits: string
}

interface Semester {
  id: string
  completedCredits: string
  totalCredits: string
  subjects: Subject[]
}

/**
 * Semesters and their subjects. The shapes match the design; the values come
 * from the study endpoint once it exists.
 */
const SEMESTERS: Semester[] = [
  { id: '2025/26/2', completedCredits: '—', totalCredits: '—', subjects: [] },
  { id: '2025/26/1', completedCredits: '—', totalCredits: '—', subjects: [] },
  { id: '2024/25/2', completedCredits: '—', totalCredits: '—', subjects: [] },
  { id: '2024/25/1', completedCredits: '—', totalCredits: '—', subjects: [] },
]

/** Level 2 summary row: semester on the left, `Xcr / Ycr` on the right. */
function SemesterSummary({ semester }: { semester: Semester }): ReactElement {
  return (
    <div data-name="panel-list" className="flex w-full items-center gap-4">
      <div className="flex min-w-px flex-1 items-start">
        <span className="hub-title-lg text-list-text min-w-px flex-1">{semester.id}</span>
        <span className="hub-label-md text-list-text-secondary shrink-0 whitespace-nowrap">
          <span className="hub-label-md-highlight text-list-text">
            {semester.completedCredits}cr{' '}
          </span>
          / {semester.totalCredits}cr
        </span>
      </div>
    </div>
  )
}

interface SubjectsPageProps {
  onBack: () => void
}

/**
 * Three levels under My Studies → Subjects:
 *   1. semester list        — Figma 2276:298670
 *   2. semester + subjects  — Figma 2276:298683
 *   3. subject detail       — Figma 2276:298694
 * Each level replaces the panel body; its header carries the back button.
 */
export function SubjectsPage({ onBack }: SubjectsPageProps): ReactElement {
  const { t } = useTranslation()
  const [semester, setSemester] = useState<Semester | null>(null)
  const [subject, setSubject] = useState<Subject | null>(null)

  // Level 3 — a single subject
  if (semester && subject) {
    return (
      <>
        <PanelHeader
          compact
          title={subject.name}
          onBack={() => {
            setSubject(null)
          }}
        />
        <PanelLinkRow label={t('studies.moodleLink')} />
        <PanelChoiceRow label={t('studies.dataSheet')} value="—" />
        <PanelChoiceRow label={t('studies.students')} value="—" />
        <PanelValueRow
          label={t('studies.credit')}
          value={<CreditValue amount={subject.credits} suffix={t('studies.cr')} />}
        />
        <PanelValueRow
          label={t('studies.courseCode')}
          value={<span className="hub-title-lg-highlight text-list-text">—</span>}
        />
        <PanelValueRow last label={t('studies.timeslot')} value="N/A" />
      </>
    )
  }

  // Level 2 — one semester and its subjects
  if (semester) {
    return (
      <>
        <PanelHeader
          compact
          title={t('studies.subjects')}
          onBack={() => {
            setSemester(null)
          }}
        />
        <SemesterSummary semester={semester} />

        {semester.subjects.length === 0 ? (
          <PanelRow last>
            <p className="hub-title-lg text-widget-secondary">{t('state.empty')}</p>
          </PanelRow>
        ) : (
          semester.subjects.map((entry, index) => (
            <PanelChoiceRow
              key={entry.id}
              label={entry.name}
              value={`${entry.credits}cr`}
              last={index === semester.subjects.length - 1}
              onOpen={() => {
                setSubject(entry)
              }}
            />
          ))
        )}
      </>
    )
  }

  // Level 1 — the semesters
  return (
    <>
      <PanelHeader compact title={t('studies.subjects')} onBack={onBack} />
      {SEMESTERS.map((entry, index) => (
        <PanelChoiceRow
          key={entry.id}
          label={entry.id}
          value={`${entry.completedCredits}cr / ${entry.totalCredits}cr`}
          last={index === SEMESTERS.length - 1}
          onOpen={() => {
            setSemester(entry)
          }}
        />
      ))}
    </>
  )
}
